<script>
/* eslint-disable vue/no-bare-strings-in-template */
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const ACELERES_API =
  typeof window !== 'undefined' &&
  window.location?.host === 'atendimento.zoraxy.ai'
    ? 'https://homolog.aceleres.ai'
    : 'https://aceleres.ai';

const GIF_REQUEST_TIMEOUT_MS = 45000;

const fetchGifRequest = async (url, options, timeoutMessage) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    GIF_REQUEST_TIMEOUT_MS
  );

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error(timeoutMessage);
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export default {
  components: { FluentIcon },
  props: {
    onSelect: { type: Function, default: () => {} },
  },
  data() {
    return {
      gifs: [],
      loading: false,
      uploading: false,
      error: null,
      statusMessage: null,
      unavailableCount: 0,
      selectedLibrary: 'all',
      uploadScope: 'user',
    };
  },
  computed: {
    visibleGifs() {
      if (this.selectedLibrary === 'all') return this.gifs;
      return this.gifs.filter(gif => gif.scope === this.selectedLibrary);
    },
  },
  mounted() {
    this.fetchGifs();
  },
  methods: {
    conversationContext() {
      const accountId = this.$store?.getters?.getCurrentAccountId;
      const chat = this.$store?.getters?.getSelectedChat;
      const conversationId =
        chat?.id ||
        this.$route?.params?.conversationId ||
        this.$route?.params?.conversation_id ||
        this.$route?.params?.id;
      const inboxId = Number(chat?.inbox_id) || null;
      return accountId && conversationId
        ? { accountId, conversationId, inboxId }
        : null;
    },
    authHeaders() {
      return {
        Accept: 'application/json',
        'X-Chatwoot-Token':
          this.$store?.getters?.getCurrentUser?.access_token || '',
      };
    },
    async fetchGifs() {
      const context = this.conversationContext();
      if (!context) {
        this.error = 'Não foi possível identificar a conversa.';
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const params = new URLSearchParams({
          chatwoot_account_id: String(context.accountId),
          chatwoot_conversation_id: String(context.conversationId),
        });
        if (context.inboxId) {
          params.set('chatwoot_inbox_id', String(context.inboxId));
        }
        const response = await fetchGifRequest(
          `${ACELERES_API}/api/stickers?${params}`,
          { headers: this.authHeaders() },
          'O carregamento das figurinhas demorou demais. Tente novamente.'
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success === false) {
          throw new Error(data?.message || 'Não foi possível carregar figurinhas.');
        }
        this.gifs = Array.isArray(data?.results) ? data.results : [];
        this.unavailableCount = Number(data?.unavailable_count) || 0;
      } catch (error) {
        this.gifs = [];
        this.unavailableCount = 0;
        this.error = error?.message || 'Não foi possível carregar figurinhas.';
      } finally {
        this.loading = false;
      }
    },
    chooseUploadScope(scope) {
      this.uploadScope = scope;
      this.error = null;
      this.statusMessage = null;
      this.$refs.gifUpload?.click();
    },
    async uploadGif(event) {
      const [file] = event.target.files || [];
      event.target.value = '';
      if (!file) return;
      const isSupportedImage =
        ['image/gif', 'image/png', 'image/jpeg', 'image/webp'].includes(
          file.type
        ) ||
        /\.(gif|png|jpe?g|webp)$/i.test(file.name);
      if (!isSupportedImage) {
        this.error =
          'Selecione um GIF ou uma imagem PNG, JPG ou WebP para converter em figurinha.';
        return;
      }
      const context = this.conversationContext();
      if (!context) {
        this.error = 'Não foi possível identificar a conversa.';
        return;
      }
      this.uploading = true;
      this.error = null;
      this.statusMessage = 'Preparando figurinha...';
      try {
        const formData = new FormData();
        formData.append('gif', file);
        formData.append('scope', this.uploadScope);
        formData.append('chatwoot_account_id', String(context.accountId));
        formData.append(
          'chatwoot_conversation_id',
          String(context.conversationId)
        );
        if (context.inboxId) {
          formData.append('chatwoot_inbox_id', String(context.inboxId));
        }
        formData.append('name', file.name.replace(/\.(gif|png|jpe?g|webp)$/i, ''));
        const response = await fetchGifRequest(
          `${ACELERES_API}/api/stickers`,
          {
            method: 'POST',
            headers: this.authHeaders(),
            body: formData,
          },
          'O upload da figurinha demorou demais. Tente novamente.'
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success === false) {
          throw new Error(data?.message || 'Não foi possível salvar a figurinha.');
        }
        await this.fetchGifs();
        this.selectedLibrary = this.uploadScope;
        this.statusMessage = 'Figurinha salva com sucesso.';
      } catch (error) {
        this.statusMessage = null;
        this.error = error?.message || 'Não foi possível salvar a figurinha.';
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<template>
  <div class="gif-picker flex flex-col h-full">
    <input
      ref="gifUpload"
      class="sr-only"
      type="file"
      accept="image/gif,image/png,image/jpeg,image/webp,.gif,.png,.jpg,.jpeg,.webp"
      @change="uploadGif"
    />

    <header class="gif-picker__header">
      <div>
        <p class="gif-picker__title">Figurinhas</p>
        <p class="gif-picker__subtitle">Escolha uma figurinha ou adicione um GIF ou imagem para converter.</p>
      </div>
    </header>

    <div class="gif-picker__upload-actions">
      <button
        type="button"
        class="gif-picker__upload"
        :disabled="uploading"
        @click="chooseUploadScope('user')"
      >
        <FluentIcon icon="arrow-upload" size="16" />
        <span
          ><strong>Só para mim</strong
          ><small>Visível apenas para você</small></span
        >
      </button>
      <button
        type="button"
        class="gif-picker__upload"
        :disabled="uploading"
        @click="chooseUploadScope('instance')"
      >
        <FluentIcon icon="people-team" size="16" />
        <span
          ><strong>Para esta instância</strong
          ><small>Disponível nesta conexão</small></span
        >
      </button>
    </div>

    <p v-if="unavailableCount" class="gif-picker__availability" role="status">
      {{
        unavailableCount > 1
          ? `${unavailableCount} figurinhas sem arquivo-fonte foram ocultadas.`
          : '1 figurinha sem arquivo-fonte foi ocultada.'
      }}
      Envie o arquivo novamente para recuperá-lo.
    </p>

    <p v-if="statusMessage" class="gif-picker__status" role="status">
      {{ statusMessage }}
    </p>

    <nav class="gif-picker__tabs" aria-label="Biblioteca de figurinhas">
      <button
        v-for="library in [
          { key: 'all', label: 'Todos' },
          { key: 'user', label: 'Meus' },
          { key: 'instance', label: 'Da instância' },
        ]"
        :key="library.key"
        type="button"
        class="gif-picker__tab"
        :class="{ 'gif-picker__tab--active': selectedLibrary === library.key }"
        @click="selectedLibrary = library.key"
      >
        {{ library.label }}
      </button>
    </nav>

    <div class="gif-picker__grid flex-1 overflow-y-auto">
      <div v-if="loading || uploading" class="gif-picker__state">
        <FluentIcon
          icon="arrow-clockwise"
          size="24"
          class="gif-picker__spinner"
        />
        <span>{{ uploading ? 'Salvando figurinha...' : 'Carregando figurinhas...' }}</span>
      </div>
      <div v-else-if="error" class="gif-picker__state">
        <FluentIcon icon="warning" size="24" />
        <span>{{ error }}</span>
        <button type="button" class="gif-picker__retry" @click="fetchGifs">
          Tentar novamente
        </button>
      </div>
      <div v-else-if="visibleGifs.length === 0" class="gif-picker__state">
        <FluentIcon icon="emoji" size="30" />
        <span>Nenhuma figurinha salva ainda</span>
        <small>Use uma das opções acima para adicionar o primeiro.</small>
      </div>
      <div v-else class="gif-picker__items">
        <button
          v-for="gif in visibleGifs"
          :key="gif.id"
          type="button"
          class="gif-picker__item"
          :title="gif.title || 'Enviar figurinha'"
          @click="onSelect(gif)"
        >
          <img
            :src="gif.preview_url || gif.gif_url"
            :alt="gif.title || 'Figurinha'"
            loading="lazy"
            class="gif-picker__img"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gif-picker {
  min-height: 0;
}
.gif-picker__header {
  @apply px-3 pt-3 pb-2;
}
.gif-picker__title {
  @apply m-0 text-sm font-semibold text-n-slate-12;
}
.gif-picker__subtitle {
  @apply m-0 mt-0.5 text-xs text-n-slate-10;
}
.gif-picker__upload-actions {
  @apply grid grid-cols-2 gap-2 px-3 pb-3;
}
.gif-picker__upload {
  @apply flex items-center gap-2 rounded-lg border border-solid border-n-slate-5 bg-n-slate-2 p-2 text-left text-n-slate-11 cursor-pointer;
}
.gif-picker__upload:hover:not(:disabled) {
  @apply border-n-brand bg-n-slate-3;
}
.gif-picker__upload:disabled {
  @apply opacity-60 cursor-wait;
}
.gif-picker__upload span {
  @apply flex flex-col min-w-0;
}
.gif-picker__upload strong {
  @apply text-xs font-medium;
}
.gif-picker__upload small {
  @apply text-[10px] text-n-slate-10 whitespace-nowrap overflow-hidden text-ellipsis;
}
.gif-picker__tabs {
  @apply flex gap-1 border-0 border-t border-solid border-n-slate-4 px-3 pt-2;
}
.gif-picker__availability {
  @apply m-0 px-3 pb-2 text-[11px] leading-4 text-n-slate-10;
}
.gif-picker__status {
  @apply m-0 px-3 pb-2 text-xs leading-4 text-n-teal-11;
}
.gif-picker__tab {
  @apply rounded px-2 py-1 text-xs text-n-slate-10 bg-transparent border-0 cursor-pointer;
}
.gif-picker__tab:hover {
  @apply bg-n-slate-3 text-n-slate-12;
}
.gif-picker__tab--active {
  @apply bg-n-brand text-white;
}
.gif-picker__grid {
  min-height: 0;
  @apply px-3 py-2;
}
.gif-picker__items {
  @apply grid gap-2;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.gif-picker__item {
  @apply flex items-center justify-center overflow-hidden rounded-lg border-0 bg-n-slate-2 p-0 cursor-pointer;
  aspect-ratio: 1 / 1;
}
.gif-picker__item:hover {
  @apply outline outline-2 outline-n-brand;
}
.gif-picker__img {
  @apply h-full w-full;
  object-fit: contain;
}
.gif-picker__state {
  @apply flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-n-slate-11;
}
.gif-picker__state small {
  @apply text-xs text-n-slate-10;
}
.gif-picker__retry {
  @apply rounded border-0 bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12 cursor-pointer;
}
.gif-picker__spinner {
  animation: gif-picker-spin 1s linear infinite;
}
@keyframes gif-picker-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
